import React, { useState } from "react";

import { COLOR_VALUES } from "shared/config/colors";
import HStack from "shared/components/layout/stack/HStack";
import SecondaryButton from "shared/components/inputs/button/SecondaryButton";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import { format } from "date-fns";
import { getCellWidthInPercent } from "features/admin/clients/ClientsTable";
import useOffices from "features/admin/offices/useOffices";

const columns = [
    {
        headCellProps: {
            text: "ID",
            width: "6.4%",
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
            tooltip: `ID: ${cell.row.original.OfficeRegionId}`,
        }),
        filter: (rows, _columnIds, filterValue) => {
            return rows.filter((row) => {
                return (
                    (filterValue === "active" && row.values.Active === true) ||
                    (filterValue === "inactive" && row.values.Active === false)
                );
            });
        },
        accessor: "OfficeRegionId",
    },
    {
        headCellProps: {
            text: "Name",
            width: "13.01%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "Name",
    },
    {
        headCellProps: {
            text: "Short N.",
            width: "8.96%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "ShortName",
    },
    {
        headCellProps: {
            text: "Country",
            width: "6.4%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "CountryName",
    },
    {
        headCellProps: {
            text: "Region",
            width: "13.01%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "RegionName",
    },
    {
        headCellProps: {
            text: "Type",
            width: "13.01%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "RegionTypeName",
    },
    {
        headCellProps: {
            text: "Holidays",
            width: "7.04%",
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "HolidayRegionName",
    },
    {
        headCellProps: {
            text: "Clients",
            centered: true,
            width: "8.4%",
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "HasClients",
    },
    {
        headCellProps: {
            text: "Groups",
            centered: true,
            width: "6.4%",
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "HasGroups",
    },
    {
        headCellProps: {
            text: "in SAP",
            centered: true,
            width: "6.4%",
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "InSAPDirectly",
    },
    {
        headCellProps: {
            text: "Offsite",
            centered: true,
            width: "6.4%",
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "IsOffSite",
    },
    {
        headCellProps: {
            text: "Opened",
            width: "8.96%",
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ? format(new Date(cell.value), "MM/dd/yyyy") : "-",
        }),
        accessor: "OpenDate",
    },
    {
        headCellProps: {
            width: getCellWidthInPercent(80),
        },
        accessor: "Active",
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            shownByRowHover: true,
            actionIcon: "edit",
            actionProps: {
                to: `?action=edit&id=${cell.row.original.OfficeRegionId}`,
                replace: true,
                "aria-label": "Edit",
            },
        }),
    },
];

const FiltersConfig = [
    {
        name: "active",
        columnAccessor: "OfficeRegionId",
        unfilteredValue: "all",
        isSegmentedControl: true,
        options: [
            {
                value: "all",
                label: "All",
            },
            {
                value: "active",
                label: "Active",
            },
            {
                value: "inactive",
                label: "Inactive",
            },
        ],
    },
];

const getRowProps = (row) => ({
    style: {
        background: row.values.Active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const tableCTAProps = {
    leadingIcon: "expand",
    label: "Add Office",
    to: "?action=add",
    replace: true,
};

const OfficesTable = () => {
    const { offices } = useOffices();

    const [filters, setFilters] = useState();

    if (offices.isLoading) {
        return <>Loading...</>;
    }

    if (offices.isError) {
        return <>Error</>;
    }

    return (
        <VStack>
            <HStack align="end" justify="space-between">
                <TableFilters
                    filtersConfig={FiltersConfig}
                    onChange={setFilters}
                />
                <SecondaryButton {...tableCTAProps} />
            </HStack>
            <Table
                columns={columns}
                data={offices.data}
                getRowProps={getRowProps}
                filters={filters}
                emptyActionProps={tableCTAProps}
            />
        </VStack>
    );
};

export default React.memo(OfficesTable);
